import { useCallback, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'

// @mui
import LoadingButton from '@mui/lab/LoadingButton'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import Divider from '@mui/material/Divider'
import DialogTitle from '@mui/material/DialogTitle'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'

// components
import { useSnackbar } from 'src/components/snackbar'
import FormProvider, { RHFTextField } from 'src/components/hook-form'
import axiosInstance from 'src/utils/axios'
import { ICardItem } from 'src/types/card'
import Iconify from 'src/components/iconify'

type Props = {
  open: boolean
  onClose: VoidFunction
  onRefleshTable: VoidFunction
  currentUser?: Partial<ICardItem>
}

export default function UserCreateEditForm({ onRefleshTable, currentUser, open, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar()

  const isEdit = !!currentUser?._id

  const defaultValues = useMemo(
    () => ({
      card: currentUser?.card || '',
      name: currentUser?.name || '',
    }),
    [currentUser],
  )

  const methods = useForm<Partial<ICardItem>>({ defaultValues })

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods

  useEffect(() => {
    if (open) reset(defaultValues)
  }, [open, defaultValues, reset])

  const onSubmit = useCallback(
    async (data: Partial<ICardItem>) => {
      try {
        if (isEdit && currentUser?._id) {
          await axiosInstance.put(`/card/${currentUser._id}`, {
            card: data.card,
            name: data.name,
          })
          enqueueSnackbar('Cartão atualizado com sucesso!', { variant: 'success' })
        } else {
          await axiosInstance.post('/card', data)
          enqueueSnackbar('Cartão criado com sucesso!', { variant: 'success' })
        }

        reset()
        onClose()
        onRefleshTable()
      } catch (error) {
        if (error?.message) {
          enqueueSnackbar(error?.message, { variant: 'error' })
        } else {
          enqueueSnackbar('Erro na requisição!', { variant: 'error' })
        }
      }
    },
    [enqueueSnackbar, onClose, reset, onRefleshTable, isEdit, currentUser],
  )

  return (
    <Dialog
      fullWidth
      maxWidth={false}
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { maxWidth: 520 } }}
    >
      <FormProvider methods={methods}>
        <DialogTitle sx={{ pb: 2 }}>
          <Stack direction='row' alignItems='center' spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                bgcolor: 'primary.lighter',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Iconify
                icon={isEdit ? 'solar:pen-bold' : 'solar:card-bold'}
                width={22}
                sx={{ color: 'primary.main' }}
              />
            </Box>
            <Box>
              <Typography variant='h6' fontWeight={700}>
                {isEdit ? 'Editar Cartão' : 'Cadastrar Cartão'}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {isEdit
                  ? 'Atualize o número ou o nome de referência'
                  : 'Adicione um novo cartão cashless'}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 3 }}>
          <Box rowGap={2.5} columnGap={2} display='grid'>
            <RHFTextField
              name='card'
              label='Código do cartão'
              placeholder='Número/código do cartão'
            />
            <RHFTextField
              name='name'
              label='Nome de referência (opcional)'
              placeholder='Ex: João da portaria, Cartão VIP 1...'
              helperText='Ajuda a identificar de quem é o cartão nos relatórios'
            />
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions>
          <Button variant='outlined' onClick={onClose}>
            Cancelar
          </Button>

          <LoadingButton
            type='button'
            onClick={handleSubmit(onSubmit)}
            variant='contained'
            loading={isSubmitting}
            startIcon={<Iconify icon='solar:check-circle-bold' />}
          >
            {isEdit ? 'Salvar' : 'Cadastrar'}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  )
}
